import type { ControllerFlowAPI, TFunction } from '@wix/yoshi-flow-editor';
import { type IWidgetControllerConfig } from '@wix/native-components-infra/dist/src/types/types';
import { createSlotVeloAPIFactory } from '@wix/widget-plugins-ooi/velo';
import {
  getAllPostsLabelTranslation,
  IS_BANNER_ENABLED_PATH,
  SECTION_HOMEPAGE,
} from '@wix/communities-blog-client-common';
import { ABOVE_POST_LIST } from '../../../../constants/ooi-slots';
import {
  fetchLastFeaturedPost,
  completeFetchLastFeaturedPost,
} from '../../../common/actions/fetch-last-featured-post';
import { pageOpened } from '../../../common/actions/page-opened';
import type { PlatformApi } from '../../../common/controller/platform-api';
import { getAppSettingsValue } from '../../../common/selectors/app-settings-base-selectors';
import { isPostContentRequired } from '../../../common/selectors/layout-selectors';
import {
  getIsValidPage,
  getLazyPaginationParams,
  getShowPagination,
} from '../../../common/selectors/pagination-selectors';
import { getFeedPosts } from '../../../common/selectors/post-selectors';
import { FEED_PAGE } from '../../../common/services/detect-route';
import { generateFeedSEOTags } from '../../../common/services/generate-seo-tags/generate-feed-seo-tags';
import { SET_APP_SETTINGS } from '../../../common/store/app-settings/app-settings-actions';
import {
  getBasicParams,
  getIsMobile,
  isEditor,
  isSSR,
  isSite,
} from '../../../common/store/basic-params/basic-params-selectors';
import { fetchProfileUrls } from '../../../common/store/profile-urls/profile-urls-actions';
import { getTranslations } from '../../../common/store/translations/translations-selectors';
import { waitForAction } from '../../../common/store/wait-for-action';
import { type AppStore, type RouteResolverFn } from '../../../common/types';
import { fetchFeedRenderModel } from '../../actions/fetch-feed-render-model';
import { ROUTE_404, type RouteHomeParams } from '../../constants/routes';
import { getLastFeaturedPost } from '../../selectors/last-featured-post-selectors';

interface CreateFeedPageRouterWithAdapterParams {
  store: AppStore;
  platformApi: PlatformApi;
  flowAPI: ControllerFlowAPI;
  controllerConfig: IWidgetControllerConfig;
  initialSiteTitle: string;
}

export const createFeedPageRouter = ({
  store,
  platformApi,
  flowAPI,
  controllerConfig,
  initialSiteTitle,
}: CreateFeedPageRouterWithAdapterParams): RouteResolverFn<RouteHomeParams> => {
  return async ({ params }, redirect, { preFetch, preFetchResult }) => {
    const state = store.getState();
    const page = parseInt(params.page || '1', 10);
    const feedPosts = getFeedPosts(state);
    const basicParams = getBasicParams(state);
    const slotAPIFactory = createSlotVeloAPIFactory(controllerConfig);
    const abovePostListSlot = slotAPIFactory?.getSlotAPI(ABOVE_POST_LIST);

    if (abovePostListSlot) {
      abovePostListSlot.category = {
        id: 'all-posts',
        label: getAllPostsLabelTranslation({
          state,
          language: basicParams.language,
        }),
        cover: null,
        description: null,
      };
    }

    const pageSize = getLazyPaginationParams({
      state,
      section: SECTION_HOMEPAGE,
      page,
    })?.size;

    const actions = preFetchResult || [
      store.dispatch(
        fetchFeedRenderModel({
          page,
          pageSize,
          includeContent: isPostContentRequired(state, SECTION_HOMEPAGE),
          includeInitialPageData: feedPosts.length === 0,
          language: basicParams.language,
        }),
      ),
      dispatchBannerFetch(preFetch ?? false),
    ];

    if (preFetch) {
      return actions;
    }

    if (preFetchResult) {
      const [, bannerPromise] = await preFetchResult;

      if (shouldFetchBanner()) {
        await store.dispatch(completeFetchLastFeaturedPost(bannerPromise));
      }

      store.dispatch(fetchProfileUrls());
    } else {
      await Promise.all(actions);
    }

    if (isInvalidPage()) {
      redirect(ROUTE_404);
    }

    await loadFeedSEOTagsIfMissing(page, basicParams.language);
    trackPageOpen();
  };

  function dispatchBannerFetch(preFetch: boolean) {
    return store.dispatch(
      waitForAction(
        [SET_APP_SETTINGS],
        () => (shouldFetchBanner() ? fetchLastFeaturedPost(preFetch) : () => {}),
        true,
      ),
    );
  }

  function shouldFetchBanner() {
    const state = store.getState();

    const isBannerSettingEnabled = getAppSettingsValue({
      state,
      key: IS_BANNER_ENABLED_PATH,
    });

    const hasNoFeaturedPosts = !getLastFeaturedPost(state);

    return isBannerSettingEnabled && hasNoFeaturedPosts;
  }

  function isInvalidPage() {
    const state = store.getState();
    const showPagination = getShowPagination(state, SECTION_HOMEPAGE);
    const isValidPage = getIsValidPage(state, SECTION_HOMEPAGE);
    return showPagination && !isValidPage;
  }

  async function loadFeedSEOTagsIfMissing(page: number, language: string) {
    const state = store.getState();

    if (!isSite(state)) {
      return;
    }

    const updatedState = store.getState();
    const translations = getTranslations(updatedState);

    flowAPI.translations.i18n.addResourceBundle(language, 'translation', translations, true, false);

    const { title, links, metaTags } = generateFeedSEOTags({
      state: updatedState,
      page,
      t: flowAPI.translations.t as TFunction,
      initialSiteTitle,
      platformApi,
    });

    await platformApi.seo.resetSEOTags();

    await Promise.allSettled([
      platformApi.seo.setLinks(links),
      platformApi.seo.setMetaTags(metaTags),
      platformApi.seo.setTitle(title),
    ]);
  }

  function trackPageOpen() {
    const state = store.getState();

    if (isSSR(state)) {
      return;
    }

    store.dispatch(
      pageOpened({
        page: FEED_PAGE,
        isMobile: getIsMobile(state),
        isEditor: isEditor(state),
      }),
    );
  }
};
